/* eslint-disable */
import React from "react";
import {
  Card,
  Grid,
  Text,
  Progress,
  Group,
  ThemeIcon,
  createStyles,
  Center,
  Skeleton,
  Stack,
  Badge,
} from "@mantine/core";
import {
  IconCalendarEvent,
  IconCalendarCheck,
  IconCalendarOff,
  // IconClockHour,
  IconArrowUpRight,
  IconArrowDownRight,
  IconAlertCircle,
  IconClockHour1,
} from "@tabler/icons-react";
import { useGetAttendanceMonthSummary } from "api/attendanceReport/getAttendanceMonth";

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: "100%",
    borderRadius: 8,
    padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: theme.white,
  },
  title: {
    color: theme.colors.gray[6],
    fontSize: 14,
    fontWeight: 600,
  },
  value: {
    fontSize: 26,
    fontWeight: 800,
    marginTop: 6,
  },
  progress: {
    marginTop: 12,
    height: 8,
    borderRadius: 8,
  },
  footer: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
  },
}));

type StatCardProps = {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  value: number | string;
  percent: number;
};

function StatCard({ icon, iconBg, title, value, percent }: StatCardProps) {
  const { classes } = useStyles();

  const hasIncrease = percent > 0;
  const absPercent = Math.min(Math.abs(Math.round(percent * 100) / 100), 100);

  return (
    <Card withBorder={false} className={classes.card}>
      <div>
        <Group position="apart" align="flex-start">
          <div>
            <Text className={classes.title}>{title}</Text>
            <Text className={classes.value}>{value}</Text>
          </div>

          <ThemeIcon
            size={48}
            radius={8}
            variant="filled"
            style={{ background: iconBg }}
          >
            {icon}
          </ThemeIcon>
        </Group>

        <Progress
          className={classes.progress}
          value={absPercent}
          radius="xl"
          color={hasIncrease ? "teal" : "red"}
        />
      </div>

      <div className={classes.footer}>
        <Center style={{ gap: 6 }}>
          {hasIncrease ? (
            <IconArrowUpRight size={16} />
          ) : (
            <IconArrowDownRight size={16} />
          )}
          <span>
            {hasIncrease ? "+" : ""}
            {percent}% from last month
          </span>
        </Center>
      </div>
    </Card>
  );
}

export default function AttendanceCard({
  month,
  year,
}: {
  month?: number;
  year?: number;
}) {
  const now = new Date();
  const m = month ?? now.getMonth() + 1;
  const y = year ?? now.getFullYear();

  const { data, isLoading, isError } = useGetAttendanceMonthSummary(m, y);

  const metrics = data?.data;

  const items = [
    {
      title: "Total Working Days",
      metric: metrics?.totalWorkingDays,
      fallback: { count: "—", percentage: 0 },
      icon: <IconCalendarEvent size={22} color="#fff" stroke={1.5} />,
      iconBg: "#2d8cff",
    },
    {
      title: "Total Leave Taken",
      metric: metrics?.totalLeaveTaken,
      fallback: { count: "—", percentage: 0 },
      icon: <IconCalendarCheck size={22} color="#fff" stroke={1.5} />,
      iconBg: "#2d8cff",
    },
    {
      title: "Total Holidays",
      metric: metrics?.totalHolidays,
      fallback: { count: "—", percentage: 0 },
      icon: <IconCalendarOff size={22} color="#fff" stroke={1.5} />,
      iconBg: "#2d8cff",
    },
    {
      title: "Total Halfdays",
      metric: metrics?.totalHalfDays,
      fallback: { count: "—", percentage: 0 },
      icon: <IconClockHour1 size={22} color="#fff" stroke={1.5} />,
      iconBg: "#2d8cff",
    },
  ];

  if (isLoading) {
    return (
      <Grid gutter="lg">
        {items.map((it) => (
          <Grid.Col xs={12} md={6} key={it.title}>
            <Skeleton height={140} radius={8} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  if (isError) {
    return (
      <Stack spacing="xs">
        <Badge color="red" leftSection={<IconAlertCircle size={14} />}>
          Failed to load attendance
        </Badge>
        <Text color="dimmed">Please try again or check your network.</Text>
      </Stack>
    );
  }

  return (
    <Grid gutter="lg">
      {items.map((it) => {
        const metric = it.metric ?? it.fallback;
        const value = metric.count;
        const percent =
          typeof metric.percentage === "number" ? metric.percentage : 0;

        return (
          <Grid.Col xs={12} md={6} key={it.title}>
            <StatCard
              title={it.title}
              value={value}
              percent={percent}
              icon={it.icon}
              iconBg={it.iconBg}
            />
          </Grid.Col>
        );
      })}
    </Grid>
  );
}
